import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import PendingIcon from '@material-ui/icons/HourglassEmpty';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PersonIcon from '@material-ui/icons/Person';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontWeight: 600
  },
  statsCard: {
    height: '100%'
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  table: {
    minWidth: 650
  },
  statusChip: {
    fontWeight: 600
  },
  searchBox: {
    marginBottom: theme.spacing(2)
  },
  filterBox: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  mapContainer: {
    width: '100%',
    height: '500px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  mapLegend: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'white',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0
    }
  },
  legendColor: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1),
    border: '2px solid #333',
    borderRadius: 4
  }
}));

// Property Lines Map Component using vanilla Leaflet
const PropertyLinesMap = ({ originalLines, suggestedLines, savedLines, classes, drawingMode, onDrawComplete, showOriginal, showSuggested, showSaved, onToggleOriginal, onToggleSuggested, onToggleSaved }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonsRef = useRef([]);
  const drawnLayerRef = useRef(null);

  // Calculate map center from suggested or original lines - returns [lat, lng] for Leaflet
  const getMapCenter = () => {
    const lines = suggestedLines?.length > 0 ? suggestedLines : originalLines;

    if (!lines || lines.length === 0) {
      return [33.4484, -112.0740]; // Default to Phoenix
    }

    // If originalLines has a different structure (from parcel data)
    if (originalLines && originalLines[0]?.coordinates) {
      const coords = originalLines[0].coordinates[0];
      if (coords && Array.isArray(coords) && coords.length > 0) {
        const firstCoord = coords[0];
        if (firstCoord && typeof firstCoord.lat === 'number' && typeof firstCoord.lng === 'number') {
          return [firstCoord.lat, firstCoord.lng];
        }
        if (Array.isArray(firstCoord) && firstCoord.length >= 2) {
          return [firstCoord[1], firstCoord[0]]; // GeoJSON is [lng, lat]
        }
      }
    }

    // For suggested lines format {lat, lng} or {latitude, longitude}
    const firstPoint = lines[0];
    if (!firstPoint) return [33.4484, -112.0740];

    const lat = firstPoint.lat || firstPoint.latitude;
    const lng = firstPoint.lng || firstPoint.longitude;

    if (typeof lat === 'number' && typeof lng === 'number') {
      return [lat, lng];
    }

    return [33.4484, -112.0740]; // Fallback
  };

  // Convert coordinates to Leaflet format [lat, lng]
  const formatCoordinates = (coords) => {
    if (!coords) return [];
    if (!Array.isArray(coords)) return [];
    if (coords.length === 0) return [];

    const result = coords.map(point => {
      // If point is in {lat, lng} format
      if (point.lat !== undefined && point.lng !== undefined) {
        return [point.lat, point.lng];
      }
      // If point is in {latitude, longitude} format
      if (point.latitude !== undefined && point.longitude !== undefined) {
        return [point.latitude, point.longitude];
      }
      // If point is an array [lng, lat] (GeoJSON format)
      if (Array.isArray(point) && point.length >= 2) {
        return [point[1], point[0]]; // Swap to [lat, lng]
      }
      return null;
    }).filter(p => p !== null && Array.isArray(p) && p.length === 2 && typeof p[0] === 'number' && typeof p[1] === 'number');

    return result;
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const mapCenter = getMapCenter();

    // Create map
    mapRef.current = L.map(mapContainerRef.current).setView(mapCenter, 18);

    // Add OpenStreetMap base layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapRef.current);

    // Add Esri satellite imagery
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri'
    }).addTo(mapRef.current);

    // Add Leaflet Geoman controls (hidden by default)
    mapRef.current.pm.addControls({
      position: 'topright',
      drawCircle: false,
      drawCircleMarker: false,
      drawMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawText: false,
      cutPolygon: false,
      rotateMode: false
    });

    // Hide controls initially
    mapRef.current.pm.removeControls();

    // Listen for drawing completion
    mapRef.current.on('pm:create', (e) => {
      // Remove previous drawn layer if exists
      if (drawnLayerRef.current && mapRef.current) {
        mapRef.current.removeLayer(drawnLayerRef.current);
      }

      drawnLayerRef.current = e.layer;

      // Get the coordinates from the drawn polygon
      const latlngs = e.layer.getLatLngs()[0]; // Get first ring of polygon
      const coordinates = latlngs.map(latlng => ({
        lat: latlng.lat,
        lng: latlng.lng
      }));

      if (onDrawComplete) {
        onDrawComplete(coordinates);
      }
    });

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Handle drawing mode changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (drawingMode) {
      // Enable drawing mode
      mapRef.current.pm.addControls({
        position: 'topright',
        drawCircle: false,
        drawCircleMarker: false,
        drawMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawText: false,
        cutPolygon: false,
        rotateMode: false
      });

      // Automatically start polygon drawing
      mapRef.current.pm.enableDraw('Polygon', {
        snappable: true,
        snapDistance: 20,
        finishOn: 'dblclick'
      });
    } else {
      // Disable drawing mode
      mapRef.current.pm.disableDraw();
      mapRef.current.pm.removeControls();
    }
  }, [drawingMode]);

  // Update polygons when data changes or visibility toggles
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing polygons
    polygonsRef.current.forEach(polygon => {
      if (mapRef.current) {
        mapRef.current.removeLayer(polygon);
      }
    });
    polygonsRef.current = [];

    // Add original property lines (red) - only if visible
    if (showOriginal && originalLines && Array.isArray(originalLines)) {
      originalLines.forEach((lineData) => {
        let coordinates = [];

        // Handle different data structures
        if (lineData.coordinates) {
          if (Array.isArray(lineData.coordinates)) {
            // If first element is an array, it's nested
            if (lineData.coordinates.length > 0 && Array.isArray(lineData.coordinates[0])) {
              coordinates = formatCoordinates(lineData.coordinates[0]);
            } else {
              coordinates = formatCoordinates(lineData.coordinates);
            }
          }
        } else if (Array.isArray(lineData)) {
          coordinates = formatCoordinates(lineData);
        }

        if (coordinates.length > 0) {
          const polygon = L.polygon(coordinates, {
            color: '#FF0000',
            fillColor: '#FF0000',
            fillOpacity: 0.2,
            weight: 3,
            opacity: 0.8
          }).addTo(mapRef.current);
          polygonsRef.current.push(polygon);
        }
      });
    }

    // Add suggested property lines (green) - only if visible
    if (showSuggested && suggestedLines && suggestedLines.length > 0) {
      const coordinates = formatCoordinates(suggestedLines);

      if (coordinates.length > 0) {
        const polygon = L.polygon(coordinates, {
          color: '#00FF00',
          fillColor: '#00FF00',
          fillOpacity: 0.3,
          weight: 3,
          opacity: 1
        }).addTo(mapRef.current);
        polygonsRef.current.push(polygon);
      }
    }

    // Add saved/approved property lines (blue) - only if visible
    if (showSaved && savedLines && savedLines.length > 0) {
      const coordinates = formatCoordinates(savedLines);

      if (coordinates.length > 0) {
        const polygon = L.polygon(coordinates, {
          color: '#0066FF',
          fillColor: '#0066FF',
          fillOpacity: 0.4,
          weight: 4,
          opacity: 1
        }).addTo(mapRef.current);
        polygonsRef.current.push(polygon);
      }
    }

    // Fit bounds if we have polygons
    if (polygonsRef.current.length > 0 && mapRef.current) {
      const group = L.featureGroup(polygonsRef.current);
      mapRef.current.fitBounds(group.getBounds().pad(0.1));
    }
  }, [originalLines, suggestedLines, savedLines, showOriginal, showSuggested, showSaved]);

  return (
    <Box position="relative">
      <div
        ref={mapContainerRef}
        style={{ height: '500px', width: '100%', borderRadius: 8, overflow: 'hidden' }}
      />

      {/* Layer Toggles */}
      <Paper className={classes.mapLegend}>
        <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
          Layer Visibility
        </Typography>
        {originalLines && originalLines.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showOriginal}
                onChange={(e) => onToggleOriginal(e.target.checked)}
                size="small"
                style={{ color: '#FF0000' }}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                <Box className={classes.legendColor} style={{ backgroundColor: 'rgba(255, 0, 0, 0.3)', marginRight: 4 }} />
                <Typography variant="body2">Original Lines</Typography>
              </Box>
            }
          />
        )}
        {suggestedLines && suggestedLines.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showSuggested}
                onChange={(e) => onToggleSuggested(e.target.checked)}
                size="small"
                style={{ color: '#00FF00' }}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                <Box className={classes.legendColor} style={{ backgroundColor: 'rgba(0, 255, 0, 0.3)', marginRight: 4 }} />
                <Typography variant="body2">Suggested Lines</Typography>
              </Box>
            }
          />
        )}
        {savedLines && savedLines.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showSaved}
                onChange={(e) => onToggleSaved(e.target.checked)}
                size="small"
                style={{ color: '#0066FF' }}
              />
            }
            label={
              <Box display="flex" alignItems="center">
                <Box className={classes.legendColor} style={{ backgroundColor: 'rgba(0, 102, 255, 0.4)', marginRight: 4 }} />
                <Typography variant="body2">Saved Lines</Typography>
              </Box>
            }
          />
        )}
      </Paper>
    </Box>
  );
};

const PropertyLineIssuesDashboard = ({ user }) => {
  const classes = useStyles();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawnCoordinates, setDrawnCoordinates] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [showSuggested, setShowSuggested] = useState(true);
  const [showSaved, setShowSaved] = useState(true);

  useEffect(() => {
    fetchIssues();
  }, [page, rowsPerPage, statusFilter]);

  const fetchIssues = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        limit: rowsPerPage,
        skip: page * rowsPerPage
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get('/api/company/propertyLineIssues', { params });

      if (response.data.success) {
        setIssues(response.data.issues);
        setTotal(response.data.total);
      } else {
        setError(response.data.message || 'Failed to fetch issues');
      }
    } catch (err) {
      console.error('Error fetching property line issues:', err);
      setError('Failed to load property line issues');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (issue) => {
    setSelectedIssue(issue);
    setResolutionNotes(issue.resolutionNotes || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedIssue(null);
    setResolutionNotes('');
    setDrawingMode(false);
    setDrawnCoordinates(null);
    setShowOriginal(true);
    setShowSuggested(true);
    setShowSaved(true);
    setDialogOpen(false);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedIssue) return;

    try {
      const response = await axios.put(`/api/company/propertyLineIssues/${selectedIssue._id}`, {
        status: newStatus,
        resolutionNotes: resolutionNotes
      });

      if (response.data.success) {
        fetchIssues();
        handleCloseDialog();
      } else {
        setError(response.data.message || 'Failed to update issue');
      }
    } catch (err) {
      console.error('Error updating issue:', err);
      setError('Failed to update issue status');
    }
  };

  const handleApproveSuggestedLines = async () => {
    if (!selectedIssue || !selectedIssue.suggestedPropertyLines) return;

    setSaving(true);
    try {
      const response = await axios.post('/api/company/propertyLineIssues/apply-decision', {
        issueId: selectedIssue._id,
        decision: 'approve',
        coordinates: selectedIssue.suggestedPropertyLines,
        resolutionNotes: resolutionNotes || 'Approved suggested property lines'
      });

      if (response.data.success) {
        fetchIssues();
        handleCloseDialog();
      } else {
        setError(response.data.message || 'Failed to approve suggested lines');
      }
    } catch (err) {
      console.error('Error approving suggested lines:', err);
      setError('Failed to approve suggested lines');
    } finally {
      setSaving(false);
    }
  };

  const handleKeepOriginalLines = async () => {
    if (!selectedIssue || !selectedIssue.originalPropertyLines) return;

    setSaving(true);
    try {
      // Extract coordinates from original property lines
      const originalCoordinates = selectedIssue.originalPropertyLines[0]?.coordinates?.[0] ||
                                  selectedIssue.originalPropertyLines[0]?.coordinates ||
                                  [];

      const response = await axios.post('/api/company/propertyLineIssues/apply-decision', {
        issueId: selectedIssue._id,
        decision: 'reject',
        coordinates: originalCoordinates,
        resolutionNotes: resolutionNotes || 'Kept original property lines'
      });

      if (response.data.success) {
        fetchIssues();
        handleCloseDialog();
      } else {
        setError(response.data.message || 'Failed to keep original lines');
      }
    } catch (err) {
      console.error('Error keeping original lines:', err);
      setError('Failed to keep original lines');
    } finally {
      setSaving(false);
    }
  };

  const handleDrawCustomLines = () => {
    setDrawingMode(true);
  };

  const handleDrawComplete = (coordinates) => {
    setDrawnCoordinates(coordinates);
    setDrawingMode(false);
  };

  const handleSaveCustomLines = async () => {
    if (!selectedIssue || !drawnCoordinates) return;

    setSaving(true);
    try {
      const response = await axios.post('/api/company/propertyLineIssues/apply-decision', {
        issueId: selectedIssue._id,
        decision: 'custom',
        coordinates: drawnCoordinates,
        resolutionNotes: resolutionNotes || 'Created custom property lines'
      });

      if (response.data.success) {
        fetchIssues();
        handleCloseDialog();
      } else {
        setError(response.data.message || 'Failed to save custom lines');
      }
    } catch (err) {
      console.error('Error saving custom lines:', err);
      setError('Failed to save custom lines');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'in_progress':
        return <WarningIcon />;
      case 'resolved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <ErrorIcon />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'in_progress':
        return 'primary';
      case 'resolved':
        return 'primary';
      case 'rejected':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const filteredIssues = issues.filter(issue =>
    issue.jobSiteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.notes.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (issue.reportedBy?.userName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: total,
    pending: issues.filter(i => i.status === 'pending').length,
    inProgress: issues.filter(i => i.status === 'in_progress').length,
    resolved: issues.filter(i => i.status === 'resolved').length
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          <LocationOnIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Property Line Issues
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchIssues} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Issues
              </Typography>
              <Typography className={classes.statsValue}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography className={classes.statsValue} style={{ color: '#ff9800' }}>
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography className={classes.statsValue} style={{ color: '#2196f3' }}>
                {stats.inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Resolved
              </Typography>
              <Typography className={classes.statsValue} style={{ color: '#4caf50' }}>
                {stats.resolved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Box p={2} className={classes.filterBox}>
          <TextField
            className={classes.searchBox}
            placeholder="Search by site name, notes, or reporter..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ flexGrow: 1 }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status Filter"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Site Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Reported At</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Has Suggested Lines</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIssues.map((issue) => (
                    <TableRow key={issue._id} hover>
                      <TableCell>{issue.jobSiteName}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(issue.status)}
                          label={issue.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(issue.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon fontSize="small" style={{ marginRight: 4 }} />
                          {issue.reportedBy?.userName || 'Unknown'}
                        </Box>
                      </TableCell>
                      <TableCell>{moment(issue.reportedAt).format('MMM D, YYYY h:mm A')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap style={{ maxWidth: 300 }}>
                          {issue.notes}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {issue.suggestedPropertyLines?.length > 0 ? (
                          <Chip label="Yes" color="primary" size="small" />
                        ) : (
                          <Chip label="No" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDialog(issue)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredIssues.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No property line issues found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        {selectedIssue && (
          <>
            <DialogTitle>
              Property Line Issue: {selectedIssue.jobSiteName}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedIssue.status)}
                    label={selectedIssue.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(selectedIssue.status)}
                    className={classes.statusChip}
                    style={{ marginTop: 8 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Reported By
                  </Typography>
                  <Typography variant="body1" style={{ marginTop: 8 }}>
                    {selectedIssue.reportedBy?.userName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedIssue.reportedBy?.userEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Reported At
                  </Typography>
                  <Typography variant="body1" style={{ marginTop: 8 }}>
                    {moment(selectedIssue.reportedAt).format('MMMM D, YYYY [at] h:mm A')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Notes
                  </Typography>
                  <Typography variant="body1" style={{ marginTop: 8 }}>
                    {selectedIssue.notes}
                  </Typography>
                </Grid>
                {(selectedIssue.originalPropertyLines?.length > 0 || selectedIssue.suggestedPropertyLines?.length > 0) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 8 }}>
                      Property Lines Comparison
                    </Typography>
                    {selectedIssue.originalPropertyLines?.length > 0 && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Original: {selectedIssue.originalPropertyLines.length} line(s)
                      </Typography>
                    )}
                    {selectedIssue.suggestedPropertyLines?.length > 0 && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Suggested: {selectedIssue.suggestedPropertyLines.length} points
                      </Typography>
                    )}
                    {selectedIssue.manualPropertyLineId?.paths?.length > 0 && (
                      <Typography variant="caption" display="block" style={{ color: '#0066FF', fontWeight: 600, marginBottom: 8 }}>
                        Saved/Approved: {selectedIssue.manualPropertyLineId.paths.length} points
                      </Typography>
                    )}
                    <PropertyLinesMap
                      originalLines={selectedIssue.originalPropertyLines}
                      suggestedLines={selectedIssue.suggestedPropertyLines}
                      savedLines={selectedIssue.manualPropertyLineId?.paths || null}
                      classes={classes}
                      drawingMode={drawingMode}
                      onDrawComplete={handleDrawComplete}
                      showOriginal={showOriginal}
                      showSuggested={showSuggested}
                      showSaved={showSaved}
                      onToggleOriginal={setShowOriginal}
                      onToggleSuggested={setShowSuggested}
                      onToggleSaved={setShowSaved}
                    />
                    {drawingMode && (
                      <Alert severity="info" style={{ marginTop: 8 }}>
                        Click on the map to draw a polygon. Double-click to finish.
                      </Alert>
                    )}
                    {drawnCoordinates && !drawingMode && (
                      <Alert severity="success" style={{ marginTop: 8 }}>
                        Custom property line drawn with {drawnCoordinates.length} points. Click "Save Custom Lines" to apply.
                      </Alert>
                    )}
                  </Grid>
                )}
                {selectedIssue.manualPropertyLineId && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Manual property line created (ID: {selectedIssue.manualPropertyLineId._id || selectedIssue.manualPropertyLineId})
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    label="Resolution Notes"
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Add notes about how this issue was resolved..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '16px 24px', flexWrap: 'wrap', gap: 8 }}>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>

              <Box style={{ flexGrow: 1 }} />

              {selectedIssue.status !== 'resolved' && !drawnCoordinates && (
                <>
                  {selectedIssue.suggestedPropertyLines?.length > 0 && (
                    <Button
                      onClick={handleApproveSuggestedLines}
                      color="primary"
                      variant="contained"
                      disabled={saving || drawingMode}
                      startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    >
                      {saving ? 'Saving...' : 'Approve Suggested Lines'}
                    </Button>
                  )}

                  {selectedIssue.originalPropertyLines?.length > 0 && (
                    <Button
                      onClick={handleKeepOriginalLines}
                      color="secondary"
                      variant="outlined"
                      disabled={saving || drawingMode}
                    >
                      Keep Original Lines
                    </Button>
                  )}

                  {!drawingMode && (
                    <Button
                      onClick={handleDrawCustomLines}
                      color="default"
                      variant="outlined"
                      disabled={saving}
                    >
                      Draw Custom Lines
                    </Button>
                  )}
                </>
              )}

              {drawnCoordinates && !drawingMode && (
                <>
                  <Button
                    onClick={() => setDrawnCoordinates(null)}
                    color="default"
                    variant="outlined"
                    disabled={saving}
                  >
                    Clear Drawing
                  </Button>
                  <Button
                    onClick={handleSaveCustomLines}
                    color="primary"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  >
                    {saving ? 'Saving...' : 'Save Custom Lines'}
                  </Button>
                </>
              )}

              {selectedIssue.status === 'resolved' && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Issue Resolved"
                  color="primary"
                />
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(PropertyLineIssuesDashboard);
